// packages/carbon/src/no-code/pulse/shallowCap.ts

function isTypedArray(value: unknown): boolean {
  return ArrayBuffer.isView(value) && !(value instanceof DataView);
}
function tagName(value: object): string {
  return (value as { constructor?: { name?: string } }).constructor?.name ?? 'Object';
}

// Clones a container (recursing via cap). Split out so cap() can bracket it with the
// inProgress/done bookkeeping that tells a true cycle apart from a shared (diamond) ref.
function cloneContainer(
  value: object,
  inProgress: WeakSet<object>,
  done: WeakMap<object, unknown>,
): unknown {
  // TypedArrays, Map, Set, plain Array — converted to plain JSON-safe structures so
  // later in-place mutations can't tear a captured snapshot (pm-002).
  if (isTypedArray(value)) {
    const result: number[] = [];
    for (const item of Array.from(value as ArrayLike<number>)) {
      result.push(item);
    }
    return result;
  }
  if (value instanceof Map) {
    const entries: unknown[] = [];
    for (const [mapKey, mapValue] of value) {
      entries.push([cap(mapKey, inProgress, done), cap(mapValue, inProgress, done)]);
    }
    return entries;
  }
  if (value instanceof Set) {
    const values: unknown[] = [];
    for (const element of value) {
      values.push(cap(element, inProgress, done));
    }
    return values;
  }
  if (Array.isArray(value)) {
    const result: unknown[] = [];
    for (const item of value as unknown[]) {
      result.push(cap(item, inProgress, done));
    }
    return result;
  }

  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return `[${tagName(value)}]`;

  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value as Record<string, unknown>)) {
    out[key] = cap((value as Record<string, unknown>)[key], inProgress, done);
  }
  return out;
}

function cap(value: unknown, inProgress: WeakSet<object>, done: WeakMap<object, unknown>): unknown {
  if (value === null || typeof value === 'number' || typeof value === 'boolean') return value;
  if (value === undefined) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'function') return '[Function]';
  if (typeof value === 'symbol') return '[Symbol]';
  if (value instanceof Promise) return '[Promise]';
  if (value instanceof Error) return { name: value.name, message: value.message };

  const obj = value as object;
  // still on the current clone stack → a real cycle, which can't be flat data, so mark it.
  if (inProgress.has(obj)) return '[Circular]';
  // reached again AFTER it finished cloning → a shared ref (a diamond, not a cycle): reuse its
  // finished clone. The old code marked this '[Circular]' too, hiding real values + faking diffs.
  if (done.has(obj)) return done.get(obj);
  if (value instanceof Date) return new Date(value.getTime());

  inProgress.add(obj);
  const result = cloneContainer(obj, inProgress, done);
  inProgress.delete(obj);
  done.set(obj, result);
  return result;
}

/** Deep, full-graph snapshot (the name is historical — it does NOT cap depth or size). Values that
 *  can't be shown as plain data become a readable placeholder (mirrors DevTools/console) so the State
 *  tab reads cleanly: true cycles -> '[Circular]'; shared (diamond) refs keep their real value;
 *  functions/symbols/Promises -> '[Function]'/'[Symbol]'/'[Promise]'; other class instances ->
 *  '[ClassName]'. Never throws. */
export function shallowCap(value: unknown): unknown {
  return cap(value, new WeakSet(), new WeakMap());
}
